import { z } from 'zod';
import { optionalDate } from './common.js';
import { isModuleKey, PLANS } from './modules.js';
/**
 * Verträge der Betreiberoberfläche (KON-032). Betreiber sind wir bei Wirkia, nicht die Benutzer eines Mandanten. Sie haben eigene Konten und eine eigene
 * Anmeldung, und was sie verwalten (Mandanten, Lizenzen, API-Schlüssel), liegt außerhalb der Mandantentrennung.
 */
export const operatorLoginInput = z.object({
    email: z.string().trim().toLowerCase().pipe(z.email('Ungültige E-Mail-Adresse')),
    password: z.string().min(1, 'Passwort fehlt').max(200),
});
/** `trial` ist ein Testmandant mit Ablaufdatum, `expired` ein abgelaufener Test (gesperrt, bis er bestellt oder gelöscht wird, KON-177 und KON-179) */
export const TENANT_STATUSES = ['active', 'trial', 'suspended', 'expired', 'cancelled'];
export const TENANT_STATUS_LABELS = { active: 'Aktiv', trial: 'Test', suspended: 'Gesperrt', expired: 'Abgelaufen', cancelled: 'Gekündigt' };
/** Rechte, die ein API-Schlüssel haben kann. Heute sendet nur die Kundenwebseite Anfragen. */
export const API_SCOPES = [
    { key: 'inquiries:create', label: 'Anfragen anlegen (Kundenwebseite)' },
    { key: 'webchat:visitor', label: 'Chat-Fenster der Kundenwebseite (Unterhaltungen von Besuchern)' },
    { key: 'marketing:subscribe', label: 'Newsletter-Anmeldungen entgegennehmen (Kundenwebseite)' },
];
const slug = z.string().trim().toLowerCase().regex(/^[a-z0-9-]{2,40}$/, 'Nur a-z, 0-9 und Bindestrich, 2 bis 40 Zeichen');
const plan = z.enum(PLANS, 'Bitte ein Paket wählen');
const addons = z
    .array(z.string())
    .max(30)
    .refine((list) => list.every(isModuleKey), 'Unbekanntes Modul')
    .transform((list) => [...new Set(list)]);
const maxUsers = z.number().int('Ganze Zahl').min(1, 'Mindestens 1').max(1000, 'Höchstens 1000');
export const createTenantInput = z.object({
    /** Interne Kennung. Ohne Angabe entsteht sie aus der Mandantennummer, angemeldet wird mit der Nummer (KON-116). */
    slug: slug.optional(),
    name: z.string().trim().min(2, 'Name fehlt').max(120),
    plan,
    addons: addons.default([]),
    /** Ohne Angabe 1 Platz (im Paket enthalten), auch bei einem Testmandanten. Weitere Plätze bucht der Mandant selbst oder der Betreiber setzt sie. */
    maxUsers: maxUsers.optional(),
    /** Letzter Tag der Lizenz (JJJJ-MM-TT), ohne Angabe unbefristet. Bei einem Testmandanten ohne Angabe: heute plus Dauer der Testphase. */
    validUntil: optionalDate,
    /** Testmandant: Status `trial` mit Ablauf nach der Testphase (KON-177) */
    trial: z.boolean().default(false),
});
export const updateTenantInput = z
    .object({
    name: z.string().trim().min(2, 'Name fehlt').max(120).optional(),
    plan: plan.optional(),
    addons: addons.optional(),
    maxUsers: maxUsers.optional(),
    /** null hebt die Befristung auf */
    validUntil: z
        .string()
        .trim()
        .nullable()
        .optional()
        .transform((v) => (v === '' ? null : v))
        .pipe(z.iso.date('Ungültiges Datum').nullable().optional()),
    status: z.enum(TENANT_STATUSES).optional(),
    /** Anmeldung mit der alten Firmenkennung noch erlauben (Übergang, KON-116) */
    legacyLogin: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const ownerInvitationInput = z.object({
    name: z.string().trim().min(2, 'Name fehlt').max(120),
    email: z.string().trim().toLowerCase().pipe(z.email('Ungültige E-Mail-Adresse')),
    /** Startrolle des Benutzers, ohne Angabe „Inhaber“ */
    roleKey: z.string().trim().regex(/^[a-z0-9_-]{2,40}$/, 'Ungültige Rolle').default('owner'),
    sendMail: z.boolean().default(false),
});
/** Die ganze Zuordnung auf einmal: Modulschlüssel mit Paket oder `addon` (nur als Zusatzmodul buchbar). Fehlende Module behalten ihre Standardzuordnung. */
export const planModulesInput = z
    .object({
    assignments: z.record(z.string(), z.enum([...PLANS, 'addon'], 'Bitte ein Paket wählen')),
})
    .strict()
    .refine((v) => Object.keys(v.assignments).every(isModuleKey), 'Unbekanntes Modul');
//# sourceMappingURL=operator.js.map